// @ts-nocheck
import { getToken, getSession, saveSession, clearSession } from './auth.js';

async function request(path, options = {}, _retried = false) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers
  };

  let res;
  try {
    res = await fetch(`/api${path}`, { ...options, headers });
  } catch (err) {
    // Network error (offline, DNS failure, etc.) — retry once after 1s
    if (!_retried && !options.signal?.aborted) {
      await new Promise(r => setTimeout(r, 1000));
      return request(path, options, true);
    }
    throw err;
  }

  // Retry once on 5xx server errors (not on 4xx client errors)
  if (res.status >= 500 && !_retried && !options.signal?.aborted) {
    await new Promise(r => setTimeout(r, 1000));
    return request(path, options, true);
  }

  let data;
  try {
    data = await res.json();
  } catch {
    if (options.signal?.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }
    throw new Error(`Server returned an invalid response (${res.status}). Please refresh and try again.`);
  }

  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/auth')) {
      // Attempt token refresh once
      let refreshed = false;
      const currentSession = getSession();
      if (currentSession?.refresh_token) {
        try {
          const refreshRes = await fetch('/api/auth/refresh', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: currentSession.refresh_token }),
          });
          if (refreshRes.ok) {
            const refreshData = await refreshRes.json();
            if (refreshData.session) { saveSession(refreshData.session, refreshData.user); refreshed = true; }
          }
        } catch { /* refresh network error — fall through to session expired */ }
      }
      if (refreshed) {
        // Retry the original request exactly once with the new token
        const newToken = getToken();
        const retryHeaders = { ...headers, ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}) };
        const retryRes = await fetch(`/api${path}`, { ...options, headers: retryHeaders });
        let retryData;
        try { retryData = await retryRes.json(); } catch { retryData = {}; }
        if (retryRes.ok) return retryData;
        // Only expire session when retry is also 401; other errors (e.g. 500) pass through normally
        if (retryRes.status !== 401) throw new Error(retryData.error || 'Request failed');
      }
      // Refresh failed or retry was 401 — show modal instead of hard redirect
      clearSession();
      window.dispatchEvent(new CustomEvent('cc:sessionExpired'));
      throw new Error('Session expired');
    }
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

export const api = {
  post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) }),
  get: (path) => request(path, { method: 'GET' }),
  patch: (path, body) => request(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: (path) => request(path, { method: 'DELETE' }),

  getMe: () => request('/auth/me'),
  checkEmail: (email) => request('/auth/check-email', { method: 'POST', body: JSON.stringify({ email }) }),
  signup: (email, password) => request('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password }) }),
  login: (email, password) => request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request('/auth/logout', { method: 'POST' }),

  resetPassword: (email) => request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ email }) }),
  changePassword: (access_token, refresh_token, new_password) => request('/auth/change-password', { method: 'POST', body: JSON.stringify({ access_token, refresh_token, new_password }) }),
  deleteAccount: () => request('/auth/delete-account', { method: 'POST' }),

  getCatalogs: () => request('/catalogs'),
  getDashboardStats: () => request('/dashboard/stats'),
  getCatalog: (id) => request(`/catalogs/${id}`),
  getCatalogJob: (id) => request(`/catalogs/${id}/job`),
  updateCatalog: (id, updates) => request(`/catalogs/${id}`, { method: 'PATCH', body: JSON.stringify(updates) }),
  deleteCatalog: (id) => request(`/catalogs/${id}`, { method: 'DELETE' }),

  getProducts: (catalogId) => request(`/catalogs/${catalogId}/products`),
  getStats: (catalogId) => request(`/catalogs/${catalogId}/stats`),

  updateVariant: (id, field, value, { signal } = {}) => request(`/variants/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ field, value }),
    signal,
  }),
  deleteVariant: (id) => request(`/variants/${id}`, { method: 'DELETE' }),
  reorderVariants: (variant_orders) => api.patch('/variants/reorder', { variant_orders }),
  reorderProducts: (product_orders) => api.patch('/products/reorder', { product_orders }),

  updateProduct: (id, updates, { signal } = {}) => request(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(updates), signal }),

  resolveFlag: (id) => request(`/flags/${id}/resolve`, { method: 'PATCH' }),

  getJobStatus: (id) => request(`/jobs/${id}/status`),
  retryJob: (catalogId) => request('/jobs/retry', { method: 'POST', body: JSON.stringify({ catalog_id: catalogId }) }),

  getCatalogWarnings: (catalogId) => request(`/catalogs/${catalogId}/warnings`),
  getCatalogFlags: (catalogId) => request(`/catalogs/${catalogId}/catalog-flags`),
  exportCatalog: (catalogId, opts = {}) => request(`/catalogs/${catalogId}/export`, { method: 'POST', body: JSON.stringify(opts) }),
  submitFeedback: (data) => request('/feedback', { method: 'POST', body: JSON.stringify(data) }),

  createProduct: (data) => request('/products', { method: 'POST', body: JSON.stringify(data) }),
  createVariant: (data) => request('/variants', { method: 'POST', body: JSON.stringify(data) }),
  bulkDeleteProducts: (productIds) => request('/products/bulk', { method: 'DELETE', body: JSON.stringify({ product_ids: productIds }) }),
  bulkUpdateProducts: (productIds, field, value, mode) => request('/products/bulk-update', { method: 'POST', body: JSON.stringify({ product_ids: productIds, field, value, mode }) }),
  bulkResolveFlags: (productIds, variantIds) => request('/flags/bulk-resolve', { method: 'PATCH', body: JSON.stringify({ product_ids: productIds, ...(variantIds ? { variant_ids: variantIds } : {}) }) }),
  bulkApplyMarkup: (productIds, markupType, markupValue, applyPrice, applyCompareAtPrice, variantIds) => request('/variants/bulk-markup', { method: 'POST', body: JSON.stringify({ product_ids: productIds, markup_type: markupType, markup_value: markupValue, apply_price: applyPrice, apply_compare_at_price: applyCompareAtPrice, ...(variantIds ? { variant_ids: variantIds } : {}) }) }),
  retryJobById: (jobId) => request(`/jobs/${jobId}/retry`, { method: 'POST' }),

  uploadImage: async (file, productId, variantId, imageAlt) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('product_id', productId);
    if (variantId) formData.append('variant_id', variantId);
    if (imageAlt) formData.append('image_alt', imageAlt);
    // Use request-style flow with token refresh on 401
    const token = getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch('/api/images/upload', { method: 'POST', headers, body: formData });
    let data;
    try { data = await res.json(); } catch { throw new Error(`Server returned an invalid response (${res.status}).`); }
    if (!res.ok) {
      if (res.status === 401) {
        // Attempt token refresh
        const currentSession = getSession();
        if (currentSession?.refresh_token) {
          try {
            const refreshRes = await fetch('/api/auth/refresh', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: currentSession.refresh_token }),
            });
            if (refreshRes.ok) {
              const refreshData = await refreshRes.json();
              if (refreshData.session) {
                saveSession(refreshData.session, refreshData.user);
                const retryRes = await fetch('/api/images/upload', {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${getToken()}` },
                  body: formData,
                });
                let retryData;
                try { retryData = await retryRes.json(); } catch { retryData = {}; }
                if (retryRes.ok) return retryData;
                if (retryRes.status !== 401) throw new Error(retryData.error || 'Upload failed');
              }
            }
          } catch { /* refresh failed — fall through */ }
        }
        clearSession();
        window.dispatchEvent(new CustomEvent('cc:sessionExpired'));
        throw new Error('Session expired');
      }
      throw new Error(data.error || 'Upload failed');
    }
    return data;
  },
  uploadImageUrl: (productId, variantId, url, imageAlt) => {
    const body = { product_id: productId, url, image_alt: imageAlt };
    if (variantId) (body as any).variant_id = variantId;
    return request('/images/upload', { method: 'POST', body: JSON.stringify(body) });
  },

  getCustomColumns: (catalogId) => request(`/custom-columns?catalog_id=${catalogId}`),
  createCustomColumn: (data) => request('/custom-columns', { method: 'POST', body: JSON.stringify(data) }),
  updateCustomColumn: (id, data) => request(`/custom-columns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deleteCustomColumn: (id) => request(`/custom-columns/${id}`, { method: 'DELETE' }),
  upsertCustomValue: (data) => request('/custom-values', { method: 'PATCH', body: JSON.stringify(data) }),
  getCustomValues: (catalogId) => request(`/custom-values?catalog_id=${catalogId}`),
  getProductsSince: (catalogId, since) => request(`/catalogs/${catalogId}/products${since ? `?since=${encodeURIComponent(since)}` : ''}`),

  getAdminExtractionHealth: () => request('/admin/extraction-health'),
  getAdminProposals: (status?: string) => request(`/admin/proposals${status ? `?status=${status}` : ''}`),
  approveProposal: (id: string) => request(`/admin/proposals/${id}/approve`, { method: 'PATCH' }),
  rejectProposal: (id: string, reason?: string) => request(`/admin/proposals/${id}/reject`, { method: 'PATCH', body: JSON.stringify({ reason }) }),
  getCircuitBreaker: () => request('/admin/circuit-breaker'),
  resetCircuitBreaker: () => request('/admin/circuit-breaker/reset', { method: 'PATCH' }),

  createCheckoutSession: (plan, cancelPath) => request('/billing/checkout-session', { method: 'POST', body: JSON.stringify({ plan, cancel_path: cancelPath }) }),
  createPortalSession: () => request('/billing/portal-session', { method: 'POST' }),

  // Multi-file upload (SmartUploader B.1)
  uploadMulti: async (files: File[], onProgress?: (pct: number) => void) => {
    const token = getToken();
    const formData = new FormData();
    for (const f of files) formData.append('files', f);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/upload');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        let data;
        try { data = JSON.parse(xhr.responseText); } catch { return reject(new Error('Invalid server response')); }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.error || 'Upload failed'));
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
  },

  selectPrimary: (dropId: string, catalogFileId: string) =>
    request(`/catalog-drops/${dropId}/select-primary`, {
      method: 'POST',
      body: JSON.stringify({ catalog_file_id: catalogFileId }),
    }),

  getCatalogDrop: (dropId: string) => request(`/catalog-drops/${dropId}`),
  getCatalogDrops: (opts?: { limit?: number; offset?: number; status?: string }) => {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.offset) params.set('offset', String(opts.offset));
    if (opts?.status) params.set('status', opts.status);
    const qs = params.toString();
    return request(`/catalog-drops${qs ? `?${qs}` : ''}`);
  },

  uploadFile: async (file, onProgress) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/upload');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.error || 'Upload failed'));
      };

      xhr.onerror = () => reject(new Error('Network error during upload'));
      xhr.send(formData);
    });
  }
};
